'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import {
  OPEN_COOKIE_SETTINGS_EVENT,
  getStoredConsent,
  storeConsent,
} from '@/lib/cookie-consent';

const PRIMARY_BUTTON =
  'flex-1 py-2.5 px-2 bg-[#801515] text-white rounded-xl text-xs font-medium hover:bg-[#601010] transition-colors';

export function CookieConsentBanner() {
  const t = useTranslations('common');
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = getStoredConsent();
    if (!consent) {
      setVisible(true);
    }

    // Re-opened from the footer "Cookie settings" link or the cookies page:
    // show the detailed view pre-filled with the current choice, so changing
    // or withdrawing consent is as easy as giving it.
    const handleOpen = () => {
      const current = getStoredConsent();
      setAnalytics(current?.analytics ?? false);
      setMarketing(current?.marketing ?? false);
      setShowCustomize(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpen);
  }, []);

  const acceptAll = () => {
    storeConsent({ analytics: true, marketing: true });
    setVisible(false);
  };

  const rejectNonEssential = () => {
    storeConsent({ analytics: false, marketing: false });
    setVisible(false);
  };

  const saveCustom = () => {
    storeConsent({ analytics, marketing });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-4 right-4 sm:right-auto sm:left-4 sm:max-w-sm z-50 animate-in slide-in-from-bottom-4 duration-500"
      // Stack above the selection bar AND, when present, the own-hold reclaim
      // banner (which also anchors just above the selection bar) so the two
      // never overlap. Both vars default to 0 when their element isn't shown.
      style={{ bottom: 'calc(1rem + var(--selection-bar-height) + var(--own-hold-banner-height, 0px))' }}
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-[#4e3529]/10 overflow-hidden">
        <div className="p-5">
          <h3 className="font-[family-name:var(--font-poppins)] font-semibold text-[#2c1810] text-sm mb-2">
            {t('cookieConsent.title')}
          </h3>
          <p className="text-xs text-[#4e3529]/65 leading-relaxed mb-4">
            {t('cookieConsent.description')}{' '}
            <Link href="/cookies" className="text-[#801515] underline hover:text-[#601010]">
              {t('cookieConsent.learnMore')}
            </Link>
          </p>

          {showCustomize && (
            <div className="space-y-3 mb-4 p-3 bg-[#faf7f4] rounded-xl">
              <label className="flex items-center gap-3 cursor-not-allowed">
                <input type="checkbox" checked disabled className="w-4 h-4 accent-[#801515] rounded" />
                <div>
                  <span className="text-xs font-medium text-[#2c1810]">{t('cookieConsent.essential')}</span>
                  <span className="text-[10px] text-[#4e3529]/45 ml-1">{t('cookieConsent.alwaysOn')}</span>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="w-4 h-4 accent-[#801515] rounded"
                />
                <div>
                  <span className="text-xs font-medium text-[#2c1810]">{t('cookieConsent.analytics')}</span>
                  <span className="text-[10px] text-[#4e3529]/45 ml-1">{t('cookieConsent.googleAnalytics')}</span>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="w-4 h-4 accent-[#801515] rounded"
                />
                <div>
                  <span className="text-xs font-medium text-[#2c1810]">{t('cookieConsent.marketing')}</span>
                  <span className="text-[10px] text-[#4e3529]/45 ml-1">{t('cookieConsent.metaPixel')}</span>
                </div>
              </label>
            </div>
          )}

          {/* Refusing must be exactly as prominent as accepting (EDPB cookie-banner
              taskforce; Romanian bill 256/2026): same size, colour and level.
              Staff approved 2026-10-01 (doc 108, Q4). */}
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <button
                onClick={showCustomize ? saveCustom : rejectNonEssential}
                className={PRIMARY_BUTTON}
              >
                {showCustomize ? t('cookieConsent.savePreferences') : t('cookieConsent.essentialOnly')}
              </button>
              <button onClick={acceptAll} className={PRIMARY_BUTTON}>
                {t('cookieConsent.acceptAll')}
              </button>
            </div>
            {!showCustomize && (
              <button
                onClick={() => setShowCustomize(true)}
                className="w-full py-2 text-[#4e3529]/70 text-xs font-medium underline underline-offset-2 hover:text-[#2c1810] transition-colors"
              >
                {t('cookieConsent.customize')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
